// src/services/vTicketService.js - VERSIÓN SIMPLIFICADA
import apiClient from './apiClient';

const vTicketService = {
  // Crear un nuevo vTicket
  createVTicket: async (vTicketData) => {
    try {
      console.log('Enviando datos de vTicket al backend:', JSON.stringify(vTicketData, null, 2));
      const response = await apiClient.post('/vtickets/createVTicket', vTicketData);
      console.log('Respuesta del backend:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error al crear vTicket:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
        data: error.response?.data
      });
      throw error;
    }
  },

  // Ejemplo de llamada desde el servicio
  getTicketsByDate: async (day, month, year) => {
    try {
      console.log(`Servicio: Buscando tickets para ${day}/${month}/${year}`);

      // Asegurar que los parámetros son strings/números válidos
      const dayParam = parseInt(day);
      const monthParam = parseInt(month);
      const yearParam = parseInt(year);

      if (isNaN(dayParam) || isNaN(monthParam) || isNaN(yearParam)) {
        throw new Error('Parámetros de fecha inválidos');
      }

      const response = await apiClient.get('/vtickets/getTicketsByDate', {
        params: {
          day: dayParam,
          month: monthParam,
          year: yearParam
        }
      });

      console.log("Respuesta del backend:", response.data);

      // Manejar diferentes formatos de respuesta
      return response.data.tickets || response.data || [];
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message;
      console.error(`Error ${status || ''} al obtener vTickets para la fecha ${day}/${month}/${year}: ${message}`);
      throw new Error(`Error al obtener tickets: ${message}`);
    }
  },



  // Obtener vTickets de un usuario específico por ID de tarjeta
  getVTicketsByIdCard: async (idCard) => {
    try {
      if (!idCard) {
        throw new Error('idCard es requerido');
      }

      const response = await apiClient.get('/vtickets/getVTicketById', {
        params: { idCard }
      });

      return Array.isArray(response.data) ? response.data : response.data.tickets || [];
    } catch (error) {
      console.error(`Error al obtener vTickets con idCard ${idCard}:`, error);

      if (error.response?.status === 404) {
        return [];
      }

      throw error;
    }
  },



  // Eliminar un vTicket individual
  deleteVTicket: async (ticketId) => {
    try {
      const response = await apiClient.delete(`/vtickets/deleteVTicket/${ticketId}`);
      return response.data;
    } catch (error) {
      console.error(`Error al eliminar vTicket ${ticketId}:`, error);
      throw error;
    }
  },

  // Obtener todos los vTickets (solo admin)
  getAllVTickets: async () => {
    try {
      const response = await apiClient.get('/vtickets/getAllVTickets');
      return response.data;
    } catch (error) {
      console.error('Error al obtener todos los vTickets:', error);
      throw error;
    }
  },

  // Actualizar un vTicket existente
  updateVTicket: async (ticketId, vTicketData) => {
    try {
      if (!ticketId) {
        throw new Error('ticketId es requerido');
      }

      const response = await apiClient.put('/vtickets/updateVTicket', vTicketData, {
        params: { id: ticketId }
      });
      return response.data;
    } catch (error) {
      console.error(`Error al actualizar vTicket ${ticketId}:`, error);
      throw error;
    }
  },

  // Cambiar el status de un vTicket
  changeTicketStatus: async (ticketId, newStatus) => {
    try {
      if (!ticketId || !newStatus) {
        throw new Error('ticketId y newStatus son requeridos');
      }

      const response = await apiClient.put('/vtickets/changeStatus', null, {
        params: { id: ticketId, newStatus }
      });
      return response.data;
    } catch (error) {
      console.error(`Error al cambiar status del vTicket ${ticketId}:`, error);
      throw error;
    }
  },

  // Obtener ticket activo del usuario
  getActiveTicket: async (idCard) => {
    try {
      if (!idCard) {
        throw new Error('idCard es requerido');
      }

      const response = await apiClient.get('/vtickets/getActiveTicket', {
        params: { idCard }
      });
      return response.data.activeTicket || response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Error al obtener ticket activo:', error);
      throw error;
    }
  },

  // Obtener vTickets por rango de fechas (filtrado en el cliente)
  getTicketsByDateRange: async (idCard, startDate, endDate) => {
    try {
      const tickets = await vTicketService.getVTicketsByIdCard(idCard);

      const start = new Date(startDate);
      const end = new Date(endDate);

      return tickets.filter(ticket => {
        const ticketDate = new Date(ticket.billDateIn || ticket.entry_time);
        return ticketDate >= start && ticketDate <= end;
      });
    } catch (error) {
      console.error(`Error al obtener vTickets en el rango ${startDate} - ${endDate}:`, error);
      throw error;
    }
  },



  // Utilidad: Formatear ticket para mostrar en UI
  formatTicketForDisplay: (ticket) => {
    if (!ticket) return null;

    return {
      id: ticket._id,
      date: new Date(ticket.billDateIn || ticket.entry_time).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      time: new Date(ticket.billDateIn || ticket.entry_time).toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      exitTime: ticket.billDateOut || ticket.exit_time ?
        new Date(ticket.billDateOut || ticket.exit_time).toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        }) : null,
      plate: ticket.vehiclesPlate || 'No especificada',
      status: ticket.status || 'Desconocido',
      amount: ticket.totalpaid || ticket.total_amount || 0,
      parking: ticket.idParking?.parkingName || 'No especificado',
      parkingAddress: ticket.idParking?.address || '',
      duration: vTicketService.calculateDuration(
        ticket.billDateIn || ticket.entry_time,
        ticket.billDateOut || ticket.exit_time
      )
    };
  },

  // Utilidad: Calcular duración del estacionamiento
  calculateDuration: (entryTime, exitTime) => {
    if (!exitTime) return 'En proceso';

    const entry = new Date(entryTime);
    const exit = new Date(exitTime);
    const diffMs = exit - entry;

    if (diffMs < 0) return 'Error en fechas';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours === 0) {
      return `${diffMinutes}m`;
    }

    return `${diffHours}h ${diffMinutes}m`;
  },

  // Utilidad: Validar datos de ticket
  validateTicketData: (ticketData) => {
    const errors = [];

    if (!ticketData.idCard) {
      errors.push('ID de tarjeta es requerido');
    }

    if (!ticketData.idParking) {
      errors.push('Parqueo es requerido');
    }

    if (!ticketData.vehiclesPlate || ticketData.vehiclesPlate.trim() === '') {
      errors.push('Placa del vehículo es requerida');
    }

    if (ticketData.totalpaid && (isNaN(ticketData.totalpaid) || ticketData.totalpaid < 0)) {
      errors.push('El monto debe ser un número válido y no negativo');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

export default vTicketService;